import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Copy, XCircle, Check } from 'lucide-react';
import { Button, Badge, Table, Modal, Input, Select } from '@/components/ui';
import { portalService } from '@/services/portalService';
import { campaignService } from '@/services/campaignService';
import type {
  Invitation,
  InvitationStatus,
  CreateInvitationRequest,
} from '@/types/portal';
import styles from './InvitationsPage.module.css';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'error' | 'info';

const STATUS_LABELS: Record<InvitationStatus, string> = {
  PENDING: 'Pendente',
  ACCEPTED: 'Aceito',
  EXPIRED: 'Expirado',
  CANCELLED: 'Cancelado',
};

const STATUS_BADGE_VARIANT: Record<InvitationStatus, BadgeVariant> = {
  PENDING: 'warning',
  ACCEPTED: 'success',
  EXPIRED: 'neutral',
  CANCELLED: 'error',
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'ACCEPTED', label: 'Aceito' },
  { value: 'EXPIRED', label: 'Expirado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

/**
 * Formats a CPF string (11 digits) as 000.000.000-00.
 */
function formatCpf(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Formats an ISO date string to pt-BR locale.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formats an ISO date string with time to pt-BR locale.
 */
function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Builds the full invitation link URL.
 */
function getInvitationLink(token: string): string {
  return `${window.location.origin}/portal/invite/${token}`;
}

/**
 * Admin page for managing patient invitations.
 *
 * Features:
 * - List all invitations with status badges (table view)
 * - Create invitation via modal with patient name, CPF, email, phone,
 *   campaign (dropdown), package (dropdown), expiration days
 * - Copy invitation link to clipboard
 * - Cancel pending invitations
 * - Filter by status and campaign
 */
export function InvitationsPage() {
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<InvitationStatus | ''>('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formCpf, setFormCpf] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formCampaignId, setFormCampaignId] = useState('');
  const [formPackageId, setFormPackageId] = useState('');
  const [formExpirationDays, setFormExpirationDays] = useState('7');
  const [formError, setFormError] = useState<string | null>(null);

  const queryParams = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(campaignFilter ? { campaignId: campaignFilter } : {}),
  };

  const {
    data: invitations = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['invitations', queryParams],
    queryFn: () => portalService.getInvitations(
      Object.keys(queryParams).length > 0 ? queryParams : undefined,
    ),
  });

  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignService.getCampaigns(),
  });

  // Get packages for selected campaign
  const selectedCampaign = campaigns.find((c) => c.id === formCampaignId);
  const campaignPackages = selectedCampaign?.packages ?? [];

  const campaignFilterOptions = [
    { value: '', label: 'Todas as campanhas' },
    ...campaigns.map((c) => ({ value: c.id, label: c.name })),
  ];

  const campaignFormOptions = [
    { value: '', label: 'Nenhuma' },
    ...campaigns.map((c) => ({ value: c.id, label: c.name })),
  ];

  const packageFormOptions = [
    { value: '', label: 'Nenhum' },
    ...campaignPackages.map((p) => ({ value: p.id, label: p.name })),
  ];

  // Filter invitations by search query (client-side)
  const filteredInvitations = invitations.filter((inv) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      inv.patientName.toLowerCase().includes(query) ||
      (inv.patientCpf && inv.patientCpf.includes(query)) ||
      (inv.patientEmail && inv.patientEmail.toLowerCase().includes(query))
    );
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateInvitationRequest) => portalService.createInvitation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      closeCreateModal();
    },
    onError: () => {
      setFormError('Erro ao criar convite. Tente novamente.');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => portalService.cancelInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const resetForm = useCallback(() => {
    setFormName('');
    setFormCpf('');
    setFormEmail('');
    setFormPhone('');
    setFormCampaignId('');
    setFormPackageId('');
    setFormExpirationDays('7');
    setFormError(null);
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setFormError(null);
  }, []);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!formName.trim()) {
        setFormError('Nome do paciente é obrigatório.');
        return;
      }

      const payload: CreateInvitationRequest = {
        patientName: formName.trim(),
        patientCpf: formCpf.replace(/\D/g, '') || undefined,
        patientEmail: formEmail.trim() || undefined,
        patientPhone: formPhone.replace(/\D/g, '') || undefined,
        campaignId: formCampaignId || undefined,
        packageId: formPackageId || undefined,
        expirationDays: parseInt(formExpirationDays) || 7,
      };

      createMutation.mutate(payload);
    },
    [
      formName,
      formCpf,
      formEmail,
      formPhone,
      formCampaignId,
      formPackageId,
      formExpirationDays,
      createMutation,
    ],
  );

  const handleCopyLink = useCallback(
    async (event: React.MouseEvent, invitation: Invitation) => {
      event.stopPropagation();
      const link = getInvitationLink(invitation.token);
      try {
        await navigator.clipboard.writeText(link);
        setCopiedId(invitation.id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = link;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopiedId(invitation.id);
        setTimeout(() => setCopiedId(null), 2000);
      }
    },
    [],
  );

  const handleCancelInvitation = useCallback(
    (event: React.MouseEvent, invitation: Invitation) => {
      event.stopPropagation();
      cancelMutation.mutate(invitation.id);
    },
    [cancelMutation],
  );

  const columns = [
    {
      key: 'patient',
      header: 'Paciente',
      render: (invitation: Invitation) => (
        <div className={styles.patientInfo}>
          <span className={styles.patientName}>{invitation.patientName}</span>
          {invitation.patientCpf && (
            <span className={styles.patientDetail}>
              {formatCpf(invitation.patientCpf)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contato',
      width: '200px',
      render: (invitation: Invitation) => (
        <div className={styles.contactInfo}>
          {invitation.patientEmail && (
            <span className={styles.contactDetail}>{invitation.patientEmail}</span>
          )}
          {invitation.patientPhone && (
            <span className={styles.contactDetail}>{invitation.patientPhone}</span>
          )}
          {!invitation.patientEmail && !invitation.patientPhone && (
            <span className={styles.contactEmpty}>-</span>
          )}
        </div>
      ),
    },
    {
      key: 'dates',
      header: 'Validade',
      width: '160px',
      render: (invitation: Invitation) => (
        <div className={styles.dateInfo}>
          <span className={styles.dateCreated}>
            Criado: {formatDate(invitation.createdAt)}
          </span>
          <span className={styles.dateExpiry}>
            Expira: {formatDate(invitation.expiresAt)}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (invitation: Invitation) => (
        <Badge variant={STATUS_BADGE_VARIANT[invitation.status]}>
          {STATUS_LABELS[invitation.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '200px',
      render: (invitation: Invitation) => (
        <div className={styles.actions}>
          {invitation.status === 'PENDING' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={
                  copiedId === invitation.id ? (
                    <Check size={14} />
                  ) : (
                    <Copy size={14} />
                  )
                }
                onClick={(e) => handleCopyLink(e, invitation)}
                aria-label="Copiar link do convite"
              >
                {copiedId === invitation.id ? 'Copiado!' : 'Copiar link'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<XCircle size={14} />}
                onClick={(e) => handleCancelInvitation(e, invitation)}
                disabled={cancelMutation.isPending}
                aria-label="Cancelar convite"
              >
                Cancelar
              </Button>
            </>
          )}
          {invitation.status === 'ACCEPTED' && invitation.acceptedAt && (
            <span className={styles.acceptedDate}>
              Aceito em {formatDateTime(invitation.acceptedAt)}
            </span>
          )}
        </div>
      ),
    },
  ];

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar convites. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Convites</h1>
          <p className={styles.subtitle}>
            Gerencie convites de pacientes para agendamento.
          </p>
        </div>
        <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
          Novo Convite
        </Button>
      </header>

      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <Input
            label="Buscar"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nome, CPF ou e-mail..."
            leftIcon={<Search size={16} />}
            fullWidth
          />
        </div>
        <div className={styles.filterGroup}>
          <Select
            label="Filtrar por status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as InvitationStatus | '')
            }
          />
          <Select
            label="Filtrar por campanha"
            options={campaignFilterOptions}
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando convites...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={filteredInvitations}
          keyExtractor={(invitation) => invitation.id}
          emptyMessage="Nenhum convite encontrado."
        />
      )}

      {/* Create invitation modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Novo Convite"
        size="md"
      >
        <form onSubmit={handleCreate} className={styles.form} noValidate>
          <Input
            label="Nome do paciente"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Nome completo"
            required
            fullWidth
          />

          <div className={styles.formRow}>
            <Input
              label="CPF"
              value={formCpf}
              onChange={(e) => setFormCpf(e.target.value)}
              placeholder="000.000.000-00"
              fullWidth
            />
            <Input
              label="Telefone"
              value={formPhone}
              onChange={(e) => setFormPhone(e.target.value)}
              placeholder="(00) 00000-0000"
              fullWidth
            />
          </div>

          <Input
            label="E-mail"
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="paciente@email.com"
            fullWidth
          />

          <div className={styles.formRow}>
            <Select
              label="Campanha"
              options={campaignFormOptions}
              value={formCampaignId}
              onChange={(e) => {
                setFormCampaignId(e.target.value);
                setFormPackageId('');
              }}
              fullWidth
            />
            <Select
              label="Pacote"
              options={packageFormOptions}
              value={formPackageId}
              onChange={(e) => setFormPackageId(e.target.value)}
              disabled={!formCampaignId}
              fullWidth
            />
          </div>

          <Input
            label="Dias de validade"
            type="number"
            value={formExpirationDays}
            onChange={(e) => setFormExpirationDays(e.target.value)}
            min={1}
            max={90}
            hint="O convite expira após esse número de dias."
            fullWidth
          />

          {formError && (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          )}

          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeCreateModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Criar convite
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
